import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Clock, Check, Phone } from "lucide-react";
import { toast } from "sonner";
import api, { formatINR } from "@/lib/api";
import { useDash } from "@/pages/dashboard/DashboardLayout";
import { Skeleton } from "@/components/ui/skeleton";

export default function PendingPaymentsView() {
  const { businessId, can } = useDash();
  const qc = useQueryClient();
  const [settling, setSettling] = useState(null); // booking_id currently choosing mode

  const { data, isLoading, isError } = useQuery({
    queryKey: ["pending-payments", businessId],
    queryFn: async () => (await api.get("/pending-payments", { params: businessId ? { business_id: businessId } : {} })).data,
    enabled: !!businessId && can?.payments !== false,
  });

  const settle = async (id, mode) => {
    try {
      await api.post(`/bookings/${id}/settle`, { mode }, { params: businessId ? { business_id: businessId } : {} });
      toast.success("Marked as fully paid");
      setSettling(null);
      qc.invalidateQueries({ queryKey: ["pending-payments"] });
      qc.invalidateQueries({ queryKey: ["bookings"] });
      qc.invalidateQueries({ queryKey: ["payments-summary"] });
    } catch (e) { toast.error("Could not update"); }
  };

  if (can?.payments === false) return (
    <div className="rounded-xl border border-slate-200 bg-white py-16 text-center shadow-pulse-sm" data-testid="pending-denied">
      <p className="font-medium text-pulse-navy">You don't have access to Pending Payments.</p>
      <p className="mt-1 text-sm text-slate">Ask the owner to enable "See payment methods" for you.</p>
    </div>
  );
  if (isLoading) return <Skeleton className="h-96 w-full rounded-xl" />;
  if (isError) return (
    <div className="rounded-xl border border-red-200 bg-red-50 py-16 text-center" data-testid="pending-error">
      <p className="font-medium text-red-700">Couldn't load pending payments.</p>
      <p className="mt-1 text-sm text-red-600">Please refresh and try again.</p>
    </div>
  );
  const s = data || { pending: [], total_pending: 0, count: 0 };

  return (
    <div className="space-y-6" data-testid="pending-payments-view">
      <div>
        <h1 className="text-2xl font-bold tracking-tight text-pulse-navy">Pending Payments</h1>
        <p className="text-sm text-slate">Every booking with a balance still to collect.</p>
      </div>

      <div className="rounded-xl border border-amber-200 bg-amber-50 p-5" data-testid="pending-total-card">
        <div className="flex items-center gap-2 text-sm font-medium text-amber-800"><Clock size={16} /> Total pending</div>
        <div className="mt-1 text-3xl font-bold text-amber-900" data-testid="pending-total">{formatINR(s.total_pending)}</div>
        <div className="mt-1 text-xs text-amber-700">{s.count} booking{s.count === 1 ? "" : "s"} awaiting balance</div>
      </div>

      {s.pending.length === 0 ? (
        <div className="rounded-xl border border-slate-200 bg-white py-16 text-center shadow-pulse-sm" data-testid="pending-empty">
          <Check className="mx-auto text-pulse-green" size={36} />
          <p className="mt-3 font-medium text-pulse-navy">All caught up — nothing pending!</p>
        </div>
      ) : (
        <div className="space-y-3">
          {s.pending.map((b) => (
            <div key={b.booking_id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-pulse-sm" data-testid={`pending-row-${b.booking_id}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="h-2.5 w-2.5 rounded-full" style={{ background: b.venue_color }} />
                    <span className="font-semibold text-pulse-navy">{b.client_name || "Client"}</span>
                    <span className="text-xs text-slate">· {b.venue_name}</span>
                  </div>
                  <div className="mt-1 text-xs text-slate">{new Date(b.date + "T00:00:00").toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short", year: "numeric" })}</div>
                  {b.client_phone ? (
                    <a href={`tel:${b.client_phone}`} className="mt-1 inline-flex items-center gap-1 text-xs font-medium text-pulse-green" data-testid={`pending-phone-${b.booking_id}`}><Phone size={12} /> {b.client_phone}</a>
                  ) : null}
                </div>
                <div className="text-right">
                  <div className="text-xs text-slate">Total {formatINR(b.amount)} · Paid {formatINR(b.advance_received)}</div>
                  <div className="text-lg font-bold text-amber-700" data-testid={`pending-amt-${b.booking_id}`}>{formatINR(b.pending_amount)} pending</div>
                </div>
              </div>
              <div className="mt-3 border-t border-slate-100 pt-3">
                {settling === b.booking_id ? (
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-slate">Balance received via:</span>
                    <button onClick={() => settle(b.booking_id, "online")} data-testid={`settle-online-${b.booking_id}`} className="rounded-md bg-pulse-green px-3 py-1.5 text-xs font-semibold text-white">Online</button>
                    <button onClick={() => settle(b.booking_id, "cash")} data-testid={`settle-cash-${b.booking_id}`} className="rounded-md bg-pulse-navy px-3 py-1.5 text-xs font-semibold text-white">Cash</button>
                    <button onClick={() => setSettling(null)} className="text-xs text-slate">Cancel</button>
                  </div>
                ) : (
                  <button onClick={() => setSettling(b.booking_id)} data-testid={`full-paid-${b.booking_id}`}
                    className="flex items-center gap-1.5 rounded-md border border-pulse-green px-4 py-2 text-sm font-semibold text-pulse-green transition-colors hover:bg-pulse-green/10">
                    <Check size={15} /> Full Payment Received
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
